import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { 
  auth, 
  googleProvider,
  appleProvider,
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  updateProfile
} from '../firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);
  const [golfer, setGolfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Listen for Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setFirebaseUser(fbUser);
      
      if (fbUser) {
        // Get Firebase ID token
        const idToken = await fbUser.getIdToken();
        setToken(idToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
        
        // Fetch user profile from backend
        try {
          const response = await axios.get(`${API_URL}/auth/me`);
          setUser(response.data.user);
          setGolfer(response.data.golfer);
        } catch (error) {
          console.error('Failed to fetch user profile:', error);
          // Try to create profile if it doesn't exist
          try {
            await axios.post(`${API_URL}/auth/create-profile`);
            const profileRes = await axios.get(`${API_URL}/auth/me`);
            setUser(profileRes.data.user);
            setGolfer(profileRes.data.golfer);
          } catch (e) {
            console.error('Failed to create profile:', e);
          }
        }
      } else {
        setToken(null);
        setUser(null);
        setGolfer(null);
        delete axios.defaults.headers.common['Authorization'];
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Refresh token periodically
  useEffect(() => {
    if (!firebaseUser) return;
    
    const refreshToken = async () => {
      const newToken = await firebaseUser.getIdToken(true);
      setToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    };

    // Refresh every 50 minutes (tokens expire in 1 hour)
    const interval = setInterval(refreshToken, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [firebaseUser]);

  const loginWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  };

  const loginWithApple = async () => {
    const result = await signInWithPopup(auth, appleProvider);
    return result.user;
  };

  const loginWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  };

  const registerWithEmail = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    // Update display name
    await updateProfile(result.user, { displayName });
    // Create profile in backend
    const idToken = await result.user.getIdToken();
    axios.defaults.headers.common['Authorization'] = `Bearer ${idToken}`;
    await axios.post(`${API_URL}/auth/create-profile`);
    return result.user;
  };

  const logout = async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setGolfer(null);
    setToken(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateGolferProfile = async (profileData) => {
    const response = await axios.put(`${API_URL}/golfers/me`, profileData);
    setGolfer(response.data);
    return response.data;
  };

  const completeOnboarding = async (profileData) => {
    const response = await axios.post(`${API_URL}/golfers/onboarding`, profileData);
    setGolfer(response.data);
    return response.data;
  };

  const refreshUserProfile = async () => {
    if (!firebaseUser) return;
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data.user);
      setGolfer(response.data.golfer);
    } catch (error) {
      console.error('Failed to refresh profile:', error);
    }
  };

  const value = {
    firebaseUser,
    user,
    golfer,
    token,
    loading,
    isAuthenticated: !!firebaseUser,
    isAdmin: user?.role === 'platform_admin',
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    registerWithEmail,
    logout,
    updateGolferProfile,
    completeOnboarding,
    refreshUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
